import {debounce} from 'vue-debounce';
import {Component, Vue} from 'vue-property-decorator';

/**
 * Class used to search for items using the search API
 */
@Component
export default class Search extends Vue {

  /** Event emitted when search is ready */
  public static readonly EVENT_NAME: string = 'searched';

  /** Current string to be searched for */
  protected searchString!: string | null;

  /** Optional request options */
  protected options!: Record<string, unknown> | null;

  /** Whether to ignore empty search requests */
  protected ignoreEmpty!: boolean;

  /** Debounced form of function */
  protected debouncedSearch = debounce(this.onSearch, 500);

  /** URL to use for the API access */
  protected apiUrl!: string;

  /**
   * Set the URL to be used for API access
   *
   * @param apiUrl
   */
  public setApiUrl(apiUrl: string): void {
    this.apiUrl = apiUrl;
  }

  /**
   * Search method, to be called by parent class
   *
   * @param searchString
   * @param options
   * @param ignoreEmpty
   */
  public searchDebounced(searchString: string | null, options: Record<string, unknown> | null = null, ignoreEmpty = true): void {
    // Debounce in order to prevent requests while typing or deleting
    this.searchString = searchString;
    this.options = options;
    this.ignoreEmpty = ignoreEmpty;
    this.debouncedSearch();
  }

  /**
   * Actual search operation
   */
  protected onSearch(): void {
    if (this.ignoreEmpty && !this.searchString) {
      this.$parent?.$emit(Search.EVENT_NAME, {
        isErrored: false,
        emptySearch: true,
      });

      return;
    }

    this.$http
        .get(this.$sfRouter.generate(this.apiUrl, Object.assign({q: this.searchString}, this.options)))
        .then((response) => {
          this.$parent?.$emit(Search.EVENT_NAME, {
            items: response.data,
            isErrored: false,
            emptySearch: false,
          });
        })
        .catch((e) => {
          console.error(e);
          this.$parent?.$emit(Search.EVENT_NAME, {
            isErrored: true,
          });
        });
  }
}

/**
 * Result from a search action
 */
export interface SearchResult {
  /** The found items */
  items?: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Whether an error occurred */
  isErrored: boolean;
  /** Whether an empty string was submitted */
  emptySearch?: boolean;
}
